import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, ArrowLeft, FunnelSimple } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { PersonLink } from '@/components/PersonLink';

export default function LeadAnalysisPage() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    api.post('/leads/leadTypes').then(r => {
      const t = r.data || [];
      setTypes(t);
      if (t.length > 0) {
        setSelectedType(t[0].TipoLead);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedType) return;
    setLoading(true);
    api.post('/leads/conversionStats', { TipoLead: selectedType })
      .then(r => { setStats(r.data?.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedType]);

  const totalLeads = stats.reduce((s, r) => s + (r.NumeroLeads || 0), 0);

  return (
    <div data-testid="lead-analysis-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <button data-testid="back-btn" onClick={() => navigate('/leads')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
        <ArrowLeft size={16} /> Voltar às Leads
      </button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Análise de Leads</h1>
        <p className="text-sm text-[#6B7280] mt-1">Estatísticas de conversão entre fases</p>
      </div>

      {/* Type selector */}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs font-medium text-[#6B7280]">Tipo de Lead:</label>
        <select data-testid="type-selector" value={selectedType} onChange={e => setSelectedType(e.target.value)}
          className="px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none">
          {types.map((t, i) => <option key={`type-${t.TipoLead || i}`} value={t.TipoLead}>{t.TipoLead}</option>)}
        </select>
      </div>

      {/* Conversion Funnel */}
      {loading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={24} className="animate-spin text-[#0F1829]" /></div>
      ) : (
        <>
          {/* Funnel visualization */}
          <div data-testid="conversion-funnel" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Funil de Conversão — {selectedType}</h3>
            <div className="space-y-2">
              {stats.map((s, i) => {
                const pct = totalLeads > 0 ? ((s.NumeroLeads || 0) / totalLeads * 100) : 0;
                const color = s.Cor || '#0F1829';
                return (
                  <div key={`stage-${s.ID_EstadoNegocio || s.Descricao || i}`} data-testid={`funnel-stage-${i}`} className="flex items-center gap-3">
                    <div className="w-[140px] text-xs font-medium text-[#374151] truncate text-right">{s.Descricao}</div>
                    <div className="flex-1 bg-[#F3F4F6] rounded-full h-7 overflow-hidden relative">
                      <div className="h-full rounded-full transition-all duration-500 flex items-center px-2"
                        style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color }}>
                        {pct > 15 && <span className="text-[10px] font-semibold text-white">{s.NumeroLeads}</span>}
                      </div>
                      {pct <= 15 && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#374151] ml-1">{s.NumeroLeads}</span>}
                    </div>
                    <div className="w-[80px] text-right">
                      <span className="text-xs font-mono text-[#6B7280]">{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bar chart */}
          {stats.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
                <h3 className="text-sm font-semibold text-[#111827] mb-4">Leads por Fase</h3>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="Descricao" type="category" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => v} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                      <Bar dataKey="NumeroLeads" radius={[0, 4, 4, 0]}>
                        {stats.map((s, i) => <Cell key={`cell-${s.ID_EstadoNegocio || s.Descricao || i}`} fill={s.Cor || '#0F1829'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Summary table */}
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
                <h3 className="text-sm font-semibold text-[#111827] mb-4">Valores Acumulados</h3>
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] pb-2">Fase</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] pb-2">Leads</th>
                    <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] pb-2">Valor</th>
                  </tr></thead>
                  <tbody>{stats.map((s, i) => (
                    <tr key={`srow-${s.ID_EstadoNegocio || s.Descricao || i}`} className="border-b border-[#F3F4F6]">
                      <td className="py-2 text-sm">
                        <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: s.Cor || '#0F1829' }} />
                        {s.Descricao}
                      </td>
                      <td className="py-2 text-sm font-mono text-right">{s.NumeroLeads || 0}</td>
                      <td className="py-2 text-sm font-mono text-right text-[#0F1829]">{fmt(s.ValorAcumulado)}</td>
                    </tr>
                  ))}</tbody>
                </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
